"use client";

import { Fragment, useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { motion } from "framer-motion";
import { ArrowRight, ChevronRight, BookOpen } from "lucide-react";
import ApoBankCta from "@/components/shared/ApoBankCta";
import FAQAccordion from "@/components/unis/FAQAccordion";
import TableOfContents, { type TocEntry } from "@/components/blog/TableOfContents";
import DownloadCTA from "@/components/shared/DownloadCTA";
import CTASection from "@/components/blog/CTASection";
import AnswerBox from "@/components/shared/AnswerBox";
import AuthorCard from "@/components/shared/AuthorCard";
import { CTA_VARIANTS, ctaForIndex } from "@/lib/cta-variants";
import { getAuthor } from "@/lib/authors";
import { fadeUp, stagger, viewportOnce } from "@/lib/motion";
import {
  audienceForCategory,
  heroFor,
  lexikonPath,
  relatedTermsFor,
  type LexikonTerm,
} from "@/lib/lexikon";

/** Brotkrumen-Navigation auf dem Bild-Hero (helle Schrift). */
function Breadcrumb({ term }: { term: string }) {
  return (
    <nav aria-label="Brotkrumen" className="mb-8">
      <ol className="flex flex-wrap items-center gap-1.5 text-sm text-paper/70">
        <li>
          <Link href="/" className="hover:text-paper transition-colors">
            Home
          </Link>
        </li>
        <ChevronRight size={14} aria-hidden="true" />
        <li>
          <Link href="/lexikon/" className="hover:text-paper transition-colors">
            Lexikon
          </Link>
        </li>
        <ChevronRight size={14} aria-hidden="true" />
        <li className="text-paper font-medium" aria-current="page">
          {term}
        </li>
      </ol>
    </nav>
  );
}

/** Detail-Vorlage für einen einzelnen Lexikon-Begriff. */
export default function LexikonDetail({ term }: { term: LexikonTerm }) {
  const related = relatedTermsFor(term);
  const hero = heroFor(term);
  const sections = term.sections ?? [];
  const hasSections = sections.length > 0;

  const tocEntries: TocEntry[] = [
    ...sections.map((s) => ({ id: s.id, label: s.heading })),
    ...(term.faqs?.length ? [{ id: "faq", label: "Häufige Fragen" }] : []),
  ];

  // Gleicher CTA-Block wie auf den Blog-Detailseiten: zufällige Startvariante
  // erst nach dem Mount (kein Hydration-Mismatch; feste Mindesthöhe der Karte
  // verhindert Layout-Shift).
  const [ctaStart, setCtaStart] = useState(0);
  useEffect(() => {
    setCtaStart(Math.floor(Math.random() * CTA_VARIANTS.length));
  }, []);
  const cta = ctaForIndex(ctaStart);
  const author = getAuthor("ncrechner Redaktion");

  return (
    <>
      {/* Bild-Hero – einheitlich für alle Begriffe */}
      <section className="relative flex min-h-[19rem] flex-col justify-end overflow-hidden bg-ink sm:min-h-[24rem]">
        <Image
          src={hero.src}
          alt={hero.alt}
          width={1600}
          height={900}
          priority
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-gradient-to-t from-ink/95 via-ink/70 to-ink/40"
        />
        <div className="relative mx-auto w-full max-w-7xl px-4 pb-14 pt-28 sm:px-6 sm:pt-32 lg:px-8">
          <Breadcrumb term={term.term} />
          {/* CSS entrance (.enter): visible from the first paint (LCP) */}
          <div className="flex max-w-3xl flex-col gap-4">
            <p className="enter flex items-center gap-3 text-xs font-bold uppercase tracking-widest text-paper/80">
              <span className="h-1.5 w-1.5 bg-accent shrink-0" aria-hidden="true" />
              {term.category}
            </p>
            <h1 className="enter [--enter-delay:90ms] font-display font-bold tracking-tight text-4xl leading-[1.1] text-paper sm:text-5xl break-words hyphens-auto">
              {term.term}
            </h1>
          </div>
        </div>
      </section>

      <section className="bg-paper">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-16">
          <AnswerBox
            question={`Was bedeutet ${term.term}?`}
            answer={term.definition}
            audience={audienceForCategory(term.category)}
            className="mb-10"
          />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Hauptinhalt */}
            <div className="min-w-0 break-words hyphens-auto lg:col-span-8">
              {hasSections ? (
                <div className="flex flex-col gap-12">
                  {(() => {
                    let ctaPos = 0;
                    return sections.map((section, idx) => {
                      const variant =
                        (idx + 1) % 3 === 0 ? ctaForIndex(ctaStart + ctaPos++) : null;
                      return (
                        <Fragment key={section.id}>
                          {/* Zusätzlicher apoBank-Anzeigenblock vor dem 2.
                              Abschnitt – gleiche Scroll-Animation wie die
                              bestehenden CTA-Blöcke. */}
                          {idx === 1 && term.apoBankCta && (
                            <motion.div
                              initial={{ opacity: 0, y: 20 }}
                              whileInView={{ opacity: 1, y: 0 }}
                              viewport={viewportOnce}
                              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                            >
                              <ApoBankCta variant={term.apoBankCta} />
                            </motion.div>
                          )}
                          <motion.section
                            id={section.id}
                            variants={fadeUp}
                      initial="hidden"
                      whileInView="visible"
                      viewport={viewportOnce}
                      className="scroll-mt-28"
                    >
                      <h2 className="font-display font-bold text-2xl sm:text-3xl text-ink mb-4 leading-tight">
                        {section.heading}
                      </h2>
                      <div className="flex flex-col gap-4">
                        {section.paragraphs.map((paragraph, i) => (
                          <p key={i} className="text-ink-soft leading-relaxed text-lg">
                            {paragraph}
                          </p>
                        ))}
                      </div>
                      {section.list && (
                        <ul className="mt-5 flex flex-col gap-3">
                          {section.list.map((item, i) => (
                            <li key={i} className="flex items-start gap-3 text-ink-soft leading-relaxed">
                              <span
                                className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent"
                                aria-hidden="true"
                              />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                          </motion.section>
                          {variant && (
                            <CTASection
                              eyebrow={variant.eyebrow}
                              heading={variant.heading}
                              description={variant.description}
                              buttonLabel={variant.buttonLabel}
                              buttonHref={variant.buttonHref}
                              appPopup={variant.appPopup ?? false}
                              backgroundImage={variant.backgroundImage}
                            />
                          )}
                        </Fragment>
                      );
                    });
                  })()}
                </div>
              ) : (
                <motion.div
                  variants={stagger(0.08)}
                  initial="hidden"
                  whileInView="visible"
                  viewport={viewportOnce}
                  className="flex flex-col gap-5"
                >
                  {term.body.map((paragraph, i) => (
                    <motion.p key={i} variants={fadeUp} className="text-ink-soft leading-relaxed text-lg">
                      {paragraph}
                    </motion.p>
                  ))}
                </motion.div>
              )}

              {/* Weiterführender Ratgeber-Link */}
              {term.article && (
                <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportOnce}>
                  <Link
                    href={term.article.href}
                    className="group mt-10 flex items-center gap-4 rounded-2xl border border-line bg-white p-5 hover:border-accent transition-colors"
                  >
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent/10 text-accent">
                      <BookOpen size={20} aria-hidden="true" />
                    </span>
                    <span className="flex-1">
                      <span className="block text-xs font-bold uppercase tracking-widest text-ink-faint">
                        Mehr im Ratgeber
                      </span>
                      <span className="block font-display font-semibold text-ink group-hover:text-accent transition-colors">
                        {term.article.label}
                      </span>
                    </span>
                    <ArrowRight
                      size={18}
                      className="shrink-0 text-ink-faint group-hover:text-accent group-hover:translate-x-0.5 transition-all"
                      aria-hidden="true"
                    />
                  </Link>
                </motion.div>
              )}

              {/* FAQ */}
              {term.faqs && term.faqs.length > 0 && (
                <motion.div
                  id="faq"
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={viewportOnce}
                  className="mt-14 scroll-mt-28"
                >
                  <h2 className="font-display font-bold text-2xl text-ink mb-6">Häufige Fragen</h2>
                  <FAQAccordion faqs={term.faqs} />
                </motion.div>
              )}

              {/* Verwandte Begriffe */}
              {related.length > 0 && (
                <motion.div
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={viewportOnce}
                  className="mt-14"
                >
                  <h2 className="font-display font-bold text-2xl text-ink mb-6">Verwandte Begriffe</h2>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {related.map((rel) => (
                      <Link
                        key={rel.slug}
                        href={lexikonPath(rel.slug)}
                        className="group flex items-start justify-between gap-3 rounded-xl border border-line bg-white p-5 hover:border-accent transition-colors"
                      >
                        <span className="min-w-0 break-words hyphens-auto">
                          <span className="block font-display font-semibold text-ink group-hover:text-accent transition-colors">
                            {rel.term}
                          </span>
                          <span className="mt-1 block text-sm text-ink-soft leading-relaxed">
                            {rel.short}
                          </span>
                        </span>
                        <ArrowRight
                          size={16}
                          className="mt-1 shrink-0 text-ink-faint group-hover:text-accent group-hover:translate-x-0.5 transition-all"
                          aria-hidden="true"
                        />
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Fallback-CTA für kurze Einträge ohne 3. Abschnitt */}
              {sections.length < 3 && (
                <CTASection
                  eyebrow={cta.eyebrow}
                  heading={cta.heading}
                  description={cta.description}
                  buttonLabel={cta.buttonLabel}
                  buttonHref={cta.buttonHref}
                  appPopup={cta.appPopup ?? false}
                  backgroundImage={cta.backgroundImage}
                  className="mt-14"
                />
              )}

              {author && <AuthorCard author={author} className="mt-14" />}

              {/* Zurück zur Übersicht */}
              <div className="mt-12">
                <Link
                  href="/lexikon/"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-accent hover:text-accent-deep transition-colors group"
                >
                  <ArrowRight
                    size={15}
                    className="rotate-180 group-hover:-translate-x-0.5 transition-transform"
                    aria-hidden="true"
                  />
                  Zurück zum Lexikon
                </Link>
              </div>
            </div>

            {/* Sidebar: Inhaltsverzeichnis (falls Abschnitte) + kompakter CTA */}
            <aside className="hidden lg:block lg:col-span-4">
              <div className="sticky top-24 flex flex-col gap-4">
                {tocEntries.length > 0 && <TableOfContents entries={tocEntries} />}
                <div className="rounded-2xl border border-line bg-white/70 p-5 shadow-sm">
                  <p className="font-display font-semibold text-ink">Deine Chancen berechnen</p>
                  <p className="mt-1 text-sm leading-relaxed text-ink-soft">
                    Sieh mit der ncrechner App, an welchen Hochschulen dein Profil reicht.
                  </p>
                  <DownloadCTA variant="accent" label="App laden" className="mt-4 w-full px-5 py-3 text-sm" />
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </>
  );
}
